import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Camera, Upload, Target, Sparkles, Calendar, Dumbbell,
  TrendingUp, AlertTriangle, RotateCw, Image as ImageIcon, Crown, Heart,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AnalyzerProgress, AnalyzerErrorCard, parseAnalyzerError } from "@/components/AnalyzerFlow";
import ExerciseLine from "@/components/ExerciseLine";

// BodyGoalCoachScreen — upload CURRENT physique + DREAM physique → AI compares
// the two and returns a realistic training + nutrition plan with a 30-day
// kickoff. Photos-first, supportive tone, never numeric scores.

function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => resolve((r.result || "").toString().split(",").pop());
    r.readAsDataURL(file);
  });
}

function PhotoSlot({ label, hint, value, previewUrl, onPick, testIdRoot }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  return (
    <div
      className="card"
      style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, position: "relative", overflow: "hidden" }}
      data-testid={`${testIdRoot}-slot`}
    >
      <div className="eyebrow" style={{ fontSize: 10 }}>{label}</div>
      <div
        style={{
          position: "relative",
          aspectRatio: "3 / 4",
          borderRadius: 12,
          background: previewUrl ? `center / cover no-repeat url(${previewUrl})` : "var(--surface-soft)",
          border: previewUrl ? "1px solid var(--gold-line)" : "1px dashed var(--line)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--text-faint)",
        }}
        data-testid={`${testIdRoot}-preview`}
      >
        {!previewUrl && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <ImageIcon size={28} strokeWidth={1.4} />
            <div className="tiny" style={{ marginTop: 8, lineHeight: 1.4 }}>{hint}</div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          ref={camRef} type="file" accept="image/*" capture="environment"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
        />
        <input
          ref={galRef} type="file" accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
        />
        <button
          type="button" className="btn btn-outline"
          style={{ flex: 1, justifyContent: "center", padding: "10px 8px", fontSize: 12 }}
          onClick={() => camRef.current?.click()}
          data-testid={`${testIdRoot}-camera-btn`}
        >
          <Camera size={13} /> Camera
        </button>
        <button
          type="button" className="btn btn-outline"
          style={{ flex: 1, justifyContent: "center", padding: "10px 8px", fontSize: 12 }}
          onClick={() => galRef.current?.click()}
          data-testid={`${testIdRoot}-gallery-btn`}
        >
          <Upload size={13} /> Gallery
        </button>
      </div>
    </div>
  );
}

export default function BodyGoalCoachScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;

  const [currentFile, setCurrentFile] = useState(null);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [dreamFile, setDreamFile] = useState(null);
  const [dreamPreview, setDreamPreview] = useState(null);

  const [notes, setNotes] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [equipment, setEquipment] = useState("home");

  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const onPickCurrent = (file) => {
    setCurrentFile(file);
    setCurrentPreview(URL.createObjectURL(file));
  };
  const onPickDream = (file) => {
    setDreamFile(file);
    setDreamPreview(URL.createObjectURL(file));
  };

  const canSubmit = currentFile && dreamFile && !loading;

  const generate = async () => {
    if (!canSubmit) return;
    setError(null);
    setPlan(null);
    setLoading(true);
    try {
      const [current_image_base64, dream_image_base64] = await Promise.all([
        fileToB64(currentFile), fileToB64(dreamFile),
      ]);
      const { data } = await api.post(
        "/fitness/body-goal",
        {
          current_image_base64,
          dream_image_base64,
          notes: notes || undefined,
          days_per_week: daysPerWeek,
          equipment,
        },
        { timeout: 90_000 },
      );
      if (data?.error === "temporary") {
        setError({ type: "server_timeout", retryable: true, message: data.message || "Please try again." });
      } else {
        setPlan(data);
      }
    } catch (err) {
      setError(parseAnalyzerError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPlan(null); setError(null);
    setCurrentFile(null); setCurrentPreview(null);
    setDreamFile(null); setDreamPreview(null);
  };

  return (
    <div className="screen with-nav fade-in" data-testid="body-goal-screen">
      <div className="top-bar">
        <button
          className="icon-btn"
          onClick={() => (plan || error) ? reset() : navigate(-1)}
          aria-label="Back"
          data-testid="body-goal-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">BODY COACH</div>
        {isPremium ? (
          <span className="premium-pill" data-testid="body-goal-quota-pill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid="body-goal-quota-pill">FREE · 2/DAY</span>
        )}
      </div>

      {!plan && !loading && !error && (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>PHOTO-BASED COACH</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 12, lineHeight: 1.12 }}>
            Where you are.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Where you&apos;re going.</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 18 }}>
            Upload your current body and a dream physique. I&apos;ll compare them honestly and design a realistic plan to get there.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <PhotoSlot
              label="CURRENT YOU"
              hint="A full-body photo in form-fitting clothes. Natural light works best."
              value={currentFile}
              previewUrl={currentPreview}
              onPick={onPickCurrent}
              testIdRoot="body-goal-current"
            />
            <PhotoSlot
              label="DREAM PHYSIQUE"
              hint="A photo of the look you're working toward. Reference image or screenshot."
              value={dreamFile}
              previewUrl={dreamPreview}
              onPick={onPickDream}
              testIdRoot="body-goal-dream"
            />
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>DAYS / WEEK</div>
            <div style={{ display: "flex", gap: 6 }}>
              {[3, 4, 5, 6].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`chip ${daysPerWeek === d ? "active" : ""}`}
                  onClick={() => setDaysPerWeek(d)}
                  data-testid={`body-goal-days-${d}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>EQUIPMENT</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {["bodyweight", "home", "bands", "gym"].map((eq) => (
                <button
                  key={eq}
                  type="button"
                  className={`chip ${equipment === eq ? "active" : ""}`}
                  onClick={() => setEquipment(eq)}
                  data-testid={`body-goal-equip-${eq}`}
                >
                  {eq}
                </button>
              ))}
            </div>
          </div>

          <div className="outfit-section">
            <div className="eyebrow" style={{ marginBottom: 10 }}>ANYTHING ELSE? (OPTIONAL)</div>
            <textarea
              className="input"
              placeholder="e.g. bad knees, vegetarian, mostly travel for work…"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 220))}
              rows={2}
              style={{ width: "100%", resize: "none", fontFamily: "inherit", lineHeight: 1.45 }}
              data-testid="body-goal-notes"
            />
          </div>

          <button
            type="button"
            className="btn btn-gold"
            onClick={generate}
            disabled={!canSubmit}
            data-testid="body-goal-generate-btn"
            style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
          >
            <Sparkles size={14} /> Build my transformation plan
          </button>
          <div className="tiny" style={{ marginTop: 10, color: "var(--text-faint)", textAlign: "center" }}>
            We never share your photos. They&apos;re used once to design your plan.
          </div>
        </>
      )}

      {loading && (
        <AnalyzerProgress
          totalSeconds={45}
          testId="body-goal-loading"
          steps={[
            "Reading your current physique…",
            "Studying your dream body…",
            "Mapping the realistic gap…",
            "Designing your training split…",
            "Setting nutrition targets…",
            "Writing your first 30 days…",
          ]}
        />
      )}

      {error && !loading && (
        <AnalyzerErrorCard
          error={error}
          onRetry={generate}
          onPickNew={reset}
          feature="body-goal"
          testId="body-goal-error"
        />
      )}

      {plan && !loading && (
        <div data-testid="body-goal-result">
          {/* Comparison hero */}
          <div className="glow-empty-hero" style={{ padding: 18 }} data-testid="body-goal-hero">
            <div className="glow-empty-badge"><Heart size={12} strokeWidth={2} /> YOUR JOURNEY</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}>
              {currentPreview && (
                <div>
                  <div className="tiny" style={{ marginBottom: 6, color: "var(--text-faint)" }}>NOW</div>
                  <div style={{ aspectRatio: "3 / 4", borderRadius: 10, background: `center / cover no-repeat url(${currentPreview})`, border: "1px solid var(--line)" }} />
                </div>
              )}
              {dreamPreview && (
                <div>
                  <div className="tiny" style={{ marginBottom: 6, color: "var(--gold)" }}>GOAL</div>
                  <div style={{ aspectRatio: "3 / 4", borderRadius: 10, background: `center / cover no-repeat url(${dreamPreview})`, border: "1px solid var(--gold-line)" }} />
                </div>
              )}
            </div>
            {plan.goal_assessment?.realistic_timeline && (
              <div className="body-sm" style={{ marginTop: 14, color: "var(--text)" }}>
                <strong style={{ color: "var(--gold)" }}>Timeline:</strong>{" "}
                {plan.goal_assessment.realistic_timeline}
                {plan.goal_assessment.feasibility && (
                  <span style={{ color: "var(--text-dim)" }}> · {plan.goal_assessment.feasibility}</span>
                )}
              </div>
            )}
          </div>

          {/* Current assessment */}
          {plan.current_assessment && (
            <div className="outfit-section" data-testid="body-goal-current-assessment">
              <div className="eyebrow" style={{ marginBottom: 10 }}>WHERE YOU ARE</div>
              <div className="card" style={{ padding: 14 }}>
                {plan.current_assessment.build_notes && (
                  <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.5 }}>
                    {plan.current_assessment.build_notes}
                  </div>
                )}
                {(plan.current_assessment.muscle_focus_now?.length > 0) && (
                  <div className="tiny" style={{ marginTop: 10, color: "var(--text-faint)" }}>
                    Already developed: {plan.current_assessment.muscle_focus_now.join(" · ")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Goal assessment */}
          {plan.goal_assessment && (
            <div className="outfit-section" data-testid="body-goal-goal-assessment">
              <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR GOAL LOOK</div>
              <div className="card" style={{ padding: 14 }}>
                {plan.goal_assessment.physique_style && (
                  <div className="h3" style={{ color: "var(--gold)", fontSize: 14 }}>{plan.goal_assessment.physique_style}</div>
                )}
                {plan.goal_assessment.key_features?.length > 0 && (
                  <ul className="bullet-list" style={{ marginTop: 8 }}>
                    {plan.goal_assessment.key_features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* The gap */}
          {plan.the_gap?.length > 0 && (
            <div className="outfit-section" data-testid="body-goal-gap">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Target size={12} strokeWidth={1.8} /> WHAT NEEDS TO CHANGE
              </div>
              <ul className="bullet-list">
                {plan.the_gap.map((g, i) => <li key={i} data-testid={`body-goal-gap-${i}`}>{g}</li>)}
              </ul>
            </div>
          )}

          {/* Training plan */}
          {plan.training_plan && (
            <div className="outfit-section" data-testid="body-goal-training">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Dumbbell size={12} strokeWidth={1.8} /> YOUR TRAINING PLAN
              </div>
              <div className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{plan.training_plan.split || "Custom split"}</div>
                <div className="tiny" style={{ marginTop: 4, color: "var(--text-faint)" }}>
                  {plan.training_plan.days_per_week} days/week · {plan.training_plan.session_length_min || 55} min sessions
                </div>
                {plan.training_plan.cardio && (
                  <div className="body-sm" style={{ marginTop: 10, color: "var(--text-dim)" }}>
                    <strong>Cardio:</strong> {plan.training_plan.cardio}
                  </div>
                )}
                {plan.training_plan.progression_rule && (
                  <div className="body-sm" style={{ marginTop: 8, color: "var(--text-dim)" }}>
                    <strong>Progress:</strong> {plan.training_plan.progression_rule}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(plan.training_plan.weekly_schedule || []).filter((d) => typeof d === "object" && d).map((d, i) => (
                  <div key={i} className="card" style={{ padding: 12 }} data-testid={`body-goal-day-${i}`}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <div style={{ width: 40, textAlign: "center", color: "var(--gold)", fontFamily: "'Fraunces', serif", fontSize: 14 }}>
                        {d.day}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="h3" style={{ color: "var(--text)", fontSize: 13 }}>{d.title}</div>
                        <div className="tiny" style={{ marginTop: 2, color: "var(--text-faint)" }}>
                          {d.focus} · {d.duration_min}min · {d.intensity}
                        </div>
                      </div>
                    </div>
                    {d.key_lifts?.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        {d.key_lifts.map((lf, k) => (
                          <ExerciseLine key={k} text={lf} testId={`body-goal-day-${i}-lift-${k}`} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nutrition */}
          {plan.nutrition && (
            <div className="outfit-section" data-testid="body-goal-nutrition">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <TrendingUp size={12} strokeWidth={1.8} /> NUTRITION
              </div>
              <div className="card" style={{ padding: 14 }}>
                {plan.nutrition.approach && (
                  <div className="h3" style={{ color: "var(--gold)", fontSize: 14 }}>{plan.nutrition.approach}</div>
                )}
                <div className="body-sm" style={{ marginTop: 8, color: "var(--text)" }}>
                  {plan.nutrition.estimated_kcal && <>≈ <strong>{plan.nutrition.estimated_kcal}</strong> kcal/day</>}
                  {plan.nutrition.protein_g_per_kg && <> · {plan.nutrition.protein_g_per_kg}g protein/kg</>}
                  {plan.nutrition.macro_split && <> · {plan.nutrition.macro_split}</>}
                </div>
                {plan.nutrition.key_foods?.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 12, marginBottom: 6, fontSize: 10 }}>STAPLE FOODS</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {plan.nutrition.key_foods.map((f, i) => (
                        <span key={i} className="chip" style={{ pointerEvents: "none" }}>{f}</span>
                      ))}
                    </div>
                  </>
                )}
                {plan.nutrition.what_to_cut?.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 12, marginBottom: 6, fontSize: 10 }}>EASE OFF</div>
                    <ul className="bullet-list">
                      {plan.nutrition.what_to_cut.map((c, i) => <li key={i} style={{ color: "var(--text-dim)" }}>{c}</li>)}
                    </ul>
                  </>
                )}
                {plan.nutrition.caveat && (
                  <div className="tiny" style={{ marginTop: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>
                    {plan.nutrition.caveat}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lifestyle */}
          {plan.lifestyle?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10 }}>HABITS THAT COMPOUND</div>
              <ul className="bullet-list">
                {plan.lifestyle.map((l, i) => <li key={i} data-testid={`body-goal-lifestyle-${i}`}>{l}</li>)}
              </ul>
            </div>
          )}

          {/* Watch outs */}
          {plan.watch_outs?.length > 0 && (
            <div className="outfit-section">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={12} strokeWidth={1.8} /> WATCH OUTS
              </div>
              <ul className="bullet-list">
                {plan.watch_outs.map((w, i) => <li key={i} style={{ color: "var(--text-dim)" }}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* First 30 days */}
          {plan.first_30_days && (
            <div className="outfit-section" data-testid="body-goal-first-30">
              <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <Calendar size={12} strokeWidth={1.8} /> YOUR FIRST 30 DAYS
              </div>
              <div className="card" style={{ padding: 14 }}>
                <div className="body" style={{ color: "var(--text)", lineHeight: 1.55 }}>{plan.first_30_days}</div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-outline"
            onClick={reset}
            data-testid="body-goal-new-btn"
            style={{ marginTop: 14, width: "100%", justifyContent: "center" }}
          >
            <RotateCw size={14} /> Start a new comparison
          </button>
        </div>
      )}
    </div>
  );
}
